'use client';
import React from 'react';

import { CarouselCardsTypes, TailwindBreakPoints } from '@src/typescriptGlobals/types';

import { RootStateTypes, useTypedAppSelector } from '@src/redux/store/store.types';

import CarouselContainer from '@src/components/Shared/CarouselContainer';
import RenderBodyCopy from '@src/components/Shared/RenderBodyCopy';
import Container from '@src/components/Shared/Container';
import SectionHeader from '@src/components/Shared/SectionHeader';
import CardCarousel from '@src/components/Shared/CardCarousel';
import CarouselIndicators from '@src/components/Shared/CarouselIndicators';

import ArrowIOS from '@src/components/Icons/ArrowIOS';

import useCarouselIndicatorAnimation from '@src/hooks/useCarouselIndicatorAnimation';
import useCarouselAnimation from '@src/hooks/useCarouselAnimation';

import { CarouselCardsPropTypes } from './CarouselCards.types';

const CarouselCards: React.FC<CarouselCardsPropTypes> = ({
  attr,
  id,
  title,
  topics,
  sectionHeaderClassName,
  sectionHeaderContainer,
}) => {
  const {
    carouselRef,
    containerRef,
    handleAddCarouselItemsToRef,
    activeIndex,
    handleShowNextItem,
    handleShowPreviousItem,
    handleJumpToSlide,
    maxIndex,
  } = useCarouselAnimation({});
  const { width } = useTypedAppSelector((state: RootStateTypes) => state.windowRXS);

  const { handleAddIndicatorRef } = useCarouselIndicatorAnimation({});

  const carouselItems = Array.isArray(topics)
    ? topics.map((item) => {
        return {
          url: item?.mediaPrimary?.url,
          pretitle: item?.preTitle,
          title: item?.title,
          description: <RenderBodyCopy className='2xl:max-w-[60rem]' bodyCopy={item?.bodyCopy?.json?.content} />,
          icon: item?.mediaSecondary?.url,
          buttons: item?.buttonCollection?.items,
        };
      })
    : [];

  return (
    <Container id={id} attr={attr} containerRef={containerRef} className='overflow-x-hidden md:overflow-x-visible!'>
      <div
        className={`relative mb-[2.7rem] flex flex-col items-center justify-between md:mb-[4.1rem] md:flex-row 2xl:mb-[7.2rem] ${sectionHeaderContainer}`}
      >
        <SectionHeader
          className='mx-auto'
          titleClassName={`text-[2.8rem] leading-[3.2rem] lg:text-[4.8rem] lg:leading-[5rem] lg:tracking-[-0.096] ${sectionHeaderClassName}`}
          title={title}
          align='left'
        />

        {width >= TailwindBreakPoints.lg && maxIndex > 0 && (
          <div className='z-10 flex justify-between'>
            <button
              aria-label='arrow'
              onClick={handleShowPreviousItem}
              disabled={activeIndex <= 0}
              className={`mr-[2rem] flex h-[5.5rem] w-[5.5rem] rotate-180 items-center justify-center rounded-full ${
                activeIndex <= 0 ? 'cursor-not-allowed' : ''
              }`}
            >
              <ArrowIOS className='' />
            </button>

            <button
              aria-label='arrow'
              onClick={handleShowNextItem}
              disabled={activeIndex >= maxIndex}
              className={`flex h-[5.5rem] w-[5.5rem] items-center justify-center rounded-full ${
                activeIndex >= maxIndex ? 'cursor-not-allowed' : ''
              }`}
            >
              <ArrowIOS className='' />
            </button>
          </div>
        )}
      </div>

      <CarouselContainer
        carouselRef={carouselRef}
        className='[&>*:not(:last-child)]:mr-[1.6rem] md:[&>*:not(:last-child)]:mr-[2.4rem]'
      >
        {carouselItems.map((carousel: CarouselCardsTypes) => {
          return (
            <CardCarousel
              carouselItemRef={handleAddCarouselItemsToRef}
              key={carousel.url}
              url={carousel.url}
              clinicTitle={carousel.pretitle}
              title={carousel.title}
              description={carousel.description}
              icon={carousel.icon}
              btns={carousel.buttons}
            />
          );
        })}
      </CarouselContainer>

      <CarouselIndicators
        className='flex justify-center'
        maxIndex={maxIndex}
        handleJumpToSlide={handleJumpToSlide}
        handleAddIndicatorRef={handleAddIndicatorRef}
        activeIndex={activeIndex}
      />
    </Container>
  );
};

export default CarouselCards;
